import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react"
import {
    View,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    ActivityIndicator,
} from "react-native"
import { useFocusEffect } from "@react-navigation/native"
import { connect } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FilledButtonM from "@common/components/button/FilledButtonM"
import ClassChipGridM from "@common/components/funnel/ClassChipGridM"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
} from "@common/components/funnel/FunnelQuestionScreen"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { apiRequest } from "@data/redux/actions/api"
import BrevetFunnelHeader from "../brevet/components/BrevetFunnelHeader"
import { useSummerFunnel } from "./context/SummerFunnelContext"
import { useSummerFunnelGuard } from "./hooks/useSummerFunnelGuard"
import { summerFunnelService } from "./services/summerFunnelService"
import {
    SUMMER_SLOT_DAYS,
    SUMMER_MONTHS,
    SUMMER_MAX_SUBJECTS,
    filterOfferedSlotIds,
    resolveFullSlotIds,
    resolveSummerPricing,
    resolveSummerFloorPrice,
    weeklyHoursFromSlots,
    subjectIconUri,
} from "./constants/summerContent"
import { formatEuros } from "../brevet/utils/formatEuros"
import SummerEyebrow from "./components/SummerEyebrow"
import SummerMonthChip from "./components/SummerMonthChip"
import SummerStartPicker from "./components/SummerStartPicker"
import SummerHelpBanner from "./components/SummerHelpBanner"
import { useSummerStartOptions } from "./hooks/useSummerStartOptions"
import { getSummerDisplayHourlyPrice } from "./utils/summerDisplayPricing"
import {
    summerMonthlyBreakdown,
    summerPerMonthSentence,
} from "./utils/summerStartPricing"

/**
 * Étape configurateur (« formule ») — un seul écran GROUPÉ, APRÈS le diagnostic
 * et AVANT le reveal du programme. On règle la formule ENSEMBLE, dans l'ordre
 * Mois → Créneaux → Matières : on cadre d'abord QUAND (mois + jours/horaires),
 * puis QUOI (matières), pour que le parent voie son tarif se construire au fil
 * des créneaux avant de choisir les matières. C'est une tâche de COMPARAISON
 * (pas de momentum) → un seul écran ajustable en direct.
 *
 * Créneaux : 5 lignes (lun→ven), chaque ligne portant les horaires d'1 h EN VENTE
 * ce jour-là. Le catalogue en compte 3 (matin 10h30-11h30, matin 11h30-12h30, soir
 * 18h30-19h30) mais un créneau saturé est MASQUÉ ici — on ne vend pas ce qu'on ne
 * peut pas staffer, et un créneau grisé « Complet » n'ajouterait qu'une frustration
 * à une étape de conversion. Les saturés viennent de la config backend
 * (`full_slot_ids`), donc fermer/rouvrir un horaire ne demande pas de build.
 * Le parent coche les créneaux voulus ; le tarif suit le NOMBRE de créneaux
 * (chaque créneau = 1 h).
 * La répartition heures/matière n'est PLUS ici — elle est devenue une option
 * discrète sur le reveal du programme (légende matière → modale).
 *
 * Contrainte produit : le NOMBRE DE CRÉNEAUX est le maître ; les matières
 * sont plafonnées à ce nombre (on peut en prendre MOINS — ex. 3 h tout en maths).
 * Rien ne bouge en douce : choisir une matière n'ajoute JAMAIS un créneau (ni ne
 * change le prix) — au plafond, les matières restantes sont grisées et pour en
 * prendre une de plus le parent ajoute lui-même un créneau. Symétriquement,
 * retirer un créneau qui passerait sous le nb de matières est bloqué (invite à
 * retirer une matière d'abord) — jamais de suppression automatique de matière.
 *
 * Visuel : même langage que les écrans de question (titre FUNNEL_TITLE_FONT_SIZE,
 * matières en chips compactes, header allégé). Colonne centrée (max ~480px).
 */

const CONTENT_MAX = 480

const SummerConfiguratorScreen = (props) => {
    const { navigation, reduxFunnelStateId } = props
    const dimensions = useContext(DimensionsContext)
    const { state, update } = useSummerFunnel()
    // Refresh-safe / URL-partageable (voir useSummerFunnelGuard).
    const { userParam, nextParams } = useSummerFunnelGuard()

    // --- Matières (chargées depuis l'API selon la classe) ---
    const [availableSubjects, setAvailableSubjects] = useState([])
    const [loadingSubjects, setLoadingSubjects] = useState(false)
    // Les chips comparent des ids en string (String(s.id)). Le contexte peut
    // contenir des ids en entier (l'endpoint programme persiste
    // checkout_params['subjects'] en intval, restauré tel quel à la reprise) → on
    // coerce en string au seed pour que les matières restaurées restent cochées
    // (sinon elles sont toutes filtrées par allowedIds, un Set de strings).
    const [subjects, setSubjects] = useState((state.subjects || []).map(String))

    // --- Créneaux / mois ---
    // selectedSlotIds = un id par créneau coché (matin et/ou soir de chaque jour),
    // tous d'1 h. AUCUNE pré-sélection à l'arrivée (ni créneau ni mois) : le prix
    // se construit par les choix du parent. Seul un état repris (contexte / lien
    // magique) réhydrate une sélection déjà faite, telle quelle.
    // Pas de filtrage des créneaux saturés ici : le Context le garantit déjà à
    // l'hydratation (cf. `withOfferedSlotIds`). Seul l'affinage par la config
    // servie par l'API reste local, plus bas.
    const [selectedSlotIds, setSelectedSlotIds] = useState(
        state.selectedSlotIds?.length ? state.selectedSlotIds : []
    )
    const [selectedMonths, setSelectedMonths] = useState(
        state.selectedMonths?.length ? state.selectedMonths : []
    )
    const [error, setError] = useState(null)

    // Config funnel (grille tarifaire API) — même source que le checkout, pour que
    // le prix affiché ici soit COHÉRENT avec celui de l'étape paiement (la grille
    // API prime ; la grille constante reste le fallback).
    const [config, setConfig] = useState(null)

    const nbSubjects = subjects.length
    // Pack d'heures hebdo = nombre de créneaux cochés (chaque créneau = 1 h).
    const weeklyHours = weeklyHoursFromSlots(selectedSlotIds)

    // Créneaux saturés (config backend prioritaire, constante en repli) → masqués.
    // `fullSlotIdsKey` ne sert QUE de dep stable aux hooks ci-dessous : le tableau
    // est recréé à chaque rendu, sa clé sérialisée ne change que sur vrai changement.
    const fullSlotIds = resolveFullSlotIds(config)
    const fullSlotIdsKey = fullSlotIds.join(",")

    // Les 5 lignes du sélecteur, chacune réduite à ses horaires EN VENTE. Un jour
    // dont tous les horaires seraient saturés disparaît de la liste plutôt que de
    // laisser une ligne vide.
    const offeredSlotDays = useMemo(() => {
        const full = new Set(fullSlotIds)
        return SUMMER_SLOT_DAYS.map((d) => ({
            ...d,
            options: d.options.filter((o) => !full.has(o.id)),
        })).filter((d) => d.options.length > 0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullSlotIdsKey])

    // La config arrive après le 1ᵉʳ rendu : si elle ferme un créneau que la
    // constante de repli croyait ouvert, on purge la sélection. Les matières ne
    // sont PAS retouchées (règle « jamais de retrait en douce ») — au pire le
    // parent est au plafond et rajoute un créneau pour en reprendre une.
    useEffect(() => {
        setSelectedSlotIds((prev) => {
            const next = filterOfferedSlotIds(prev, config)
            return next.length === prev.length ? prev : next
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullSlotIdsKey])

    // Re-sème les sélections locales depuis le contexte à chaque focus (back-nav /
    // reprise via lien magique) — le contexte est la source de vérité. Il garantit
    // déjà l'absence de créneau saturé d'après la constante ; on repasse le filtre
    // avec la config servie, qui peut en fermer davantage.
    useFocusEffect(
        useCallback(() => {
            if (state.subjects?.length) setSubjects(state.subjects.map(String))
            if (state.selectedSlotIds?.length)
                setSelectedSlotIds(
                    filterOfferedSlotIds(state.selectedSlotIds, config)
                )
            if (state.selectedMonths?.length)
                setSelectedMonths(state.selectedMonths)
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [state.subjects, state.selectedSlotIds, state.selectedMonths])
    )

    useEffect(() => {
        amplitude.track("summer_funnel_configurator_viewed", {
            funnel_state_id: state.funnelStateId,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Sans classe → impossible de proposer des matières → on renvoie au début.
    useEffect(() => {
        if (!state.studentClass && !userParam) {
            navigation.navigate("summer/start")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Récupère la config (grille tarifaire API) → prix cohérent avec le checkout.
    useEffect(() => {
        if (!state.funnelStateId) return
        let cancelled = false
        summerFunnelService
            .getFunnelState({ stateId: state.funnelStateId })
            .then((res) => {
                if (cancelled) return
                const payload = res?.data?.data ?? res?.data ?? {}
                setConfig(payload.config ?? null)
            })
            .catch(() => {
                /* fallback sur la grille constante si la config ne charge pas */
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.funnelStateId])

    // Récupère les matières proposables pour la classe (chapitres validés, triées).
    useEffect(() => {
        if (!state.studentClass) return
        let cancelled = false
        setLoadingSubjects(true)
        summerFunnelService
            .getSubjects({ studentClass: state.studentClass })
            .then((res) => {
                if (cancelled) return
                const list = res?.data?.data ?? res?.data ?? []
                const fetched = Array.isArray(list) ? list : []
                setAvailableSubjects(fetched)
                // Filtre les matières persistées sur celles réellement proposées —
                // mais UNIQUEMENT contre un catalogue non vide. Un fetch
                // transitoirement vide (ou une classe non encore normalisée) ne doit
                // jamais effacer les matières reprises (qui seraient ensuite
                // persistées vides au « Voir le programme »).
                if (fetched.length) {
                    const allowedIds = new Set(fetched.map((s) => String(s.id)))
                    setSubjects((prev) => prev.filter((id) => allowedIds.has(id)))
                }
            })
            .catch(() => {
                if (!cancelled) setAvailableSubjects([])
            })
            .finally(() => {
                if (!cancelled) setLoadingSubjects(false)
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.studentClass])

    const toggleMonth = (id) => {
        setError(null)
        // Décocher est toujours permis (y compris le dernier mois) : l'exigence
        // « au moins un mois » est portée par le CTA (bloqué tant que la
        // sélection est incomplète), plus par le toggle.
        setSelectedMonths((prev) =>
            prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
        )
    }

    // Coche / décoche un créneau (matin ou soir d'un jour). Le nombre de créneaux
    // est le MAÎTRE (il pilote heures + prix). Symétrie du plafond « matières ≤
    // créneaux » : on ne retire JAMAIS une matière en douce — si enlever ce
    // créneau passait sous le nb de matières, on bloque et on invite à retirer
    // une matière d'abord.
    const toggleSlot = (slotId) => {
        setError(null)
        if (selectedSlotIds.includes(slotId)) {
            const nextSlotIds = selectedSlotIds.filter((s) => s !== slotId)
            if (subjects.length > weeklyHoursFromSlots(nextSlotIds)) {
                setError("slot_locked_by_subject")
                return
            }
            setSelectedSlotIds(nextSlotIds)
        } else {
            setSelectedSlotIds((prev) => [...prev, slotId])
        }
    }

    const toggleSubject = (id) => {
        setError(null)
        if (subjects.includes(id)) {
            setSubjects(subjects.filter((s) => s !== id))
            return
        }
        // Plafond : autant de matières que de créneaux (chaque matière ≥ 1 h).
        // On n'ajoute JAMAIS un créneau à la place de l'utilisateur — pour une
        // matière de plus, il choisit lui-même un créneau (le prix suit CE choix,
        // jamais un ajout au hasard). Au plafond, les chips sont grisées.
        if (
            subjects.length >= weeklyHours ||
            subjects.length >= SUMMER_MAX_SUBJECTS
        ) {
            return
        }
        setSubjects([...subjects, id])
    }

    // Prix mensuel = fonction du nombre de créneaux ET de la variante d'engagement
    // (servie par le lien fast-track admin ; défaut SANS engagement). La grille API
    // (config) prime ; la grille constante sert de fallback. Tant qu'AUCUN créneau
    // n'est coché, on affiche le prix plancher « à partir de » dérivé de la MÊME
    // grille (jamais une constante dédiée).
    const hasSlots = weeklyHours > 0
    const pricing = resolveSummerPricing(
        weeklyHours,
        !!state.withCommitment,
        config?.pricing_grid
    )
    const displayPricing = {
        ...pricing,
        // Display only: summer has four effective teaching weeks. Do not reuse
        // the subscription/proration grid here (P-2339 owns that correction).
        hourly: getSummerDisplayHourlyPrice(pricing?.price, weeklyHours),
    }
    const floorPrice = resolveSummerFloorPrice(
        !!state.withCommitment,
        config?.pricing_grid
    )

    // Sélection complète = ≥ 1 mois + ≥ 1 créneau + ≥ 1 matière. Sans
    // pré-sélection, le CTA reste bloqué tant que le parent n'a pas tout choisi.
    const selectionComplete =
        selectedMonths.length > 0 && hasSlots && nbSubjects > 0

    // Guidage sous le CTA désactivé : nomme ce qui reste à cocher (mois →
    // créneau → matière, dans l'ordre des sections). Sans ce message, un bouton
    // gris muet ne dit pas au parent ce qui manque — friction de conversion.
    const missingSelectionHint = selectionComplete
        ? null
        : selectedMonths.length < 1
          ? "Choisissez au moins un mois pour continuer."
          : !hasSlots
            ? "Choisissez au moins un créneau pour continuer."
            : "Choisissez au moins une matière pour continuer."

    // Note de tarif : « N h/semaine · <engagement> » avec créneaux, sinon le
    // seul libellé d'engagement (capitalisé en tête de phrase).
    const engagementLabel = state.withCommitment
        ? "avec engagement 12 mois"
        : "sans engagement"
    const priceNoteLead = hasSlots
        ? `${weeklyHours} h/semaine · ${engagementLabel}`
        : engagementLabel.charAt(0).toUpperCase() + engagementLabel.slice(1)

    // Options de date de démarrage (backend, jamais recalculées ici) — suivent
    // la sélection courante. Le choix pilote le prorata de la 1ʳᵉ facture.
    const {
        options: startOptions,
        selectedOption: selectedStartOption,
        monthlyPriceCents: startMonthlyPriceCents,
    } = useSummerStartOptions({
        stateId: state.funnelStateId || reduxFunnelStateId,
        slotIds: selectedSlotIds,
        months: selectedMonths,
        withCommitment: !!state.withCommitment,
        startDate: state.startDate,
    })

    // « Ce que vous payez par mois » pour la date retenue — même source que le
    // récap du picker. Tant que les options n'ont pas chargé, on retombe sur le
    // tarif plein d'un mois (pas de saut de layout, énoncé neutre).
    const perMonthSentence =
        summerPerMonthSentence(
            summerMonthlyBreakdown(
                selectedStartOption,
                startMonthlyPriceCents,
                selectedMonths
            )
        ) || `${formatEuros(pricing.price)} par mois`

    const onSelectStartOption = (option) => {
        amplitude.track("summer_funnel_start_option_selected", {
            funnel_state_id: state.funnelStateId,
            start_date: option.start_date,
        })
        update({
            startDate: option.start_date,
            firstChargeAmountCents: option.first_charge_amount_cents,
            firstSession: option.first_session,
        })
    }

    const onContinue = () => {
        // Le CTA n'est actif que si la sélection est complète (mois + créneau +
        // matière) — pas de re-validation ici, le guidage sous le bouton nomme
        // déjà ce qui manque.
        // Le choix effectif de démarrage = l'option résolue (le backend a pu
        // retomber sur la plus proche si l'ancien choix n'est plus valide).
        const startDate = selectedStartOption?.start_date || state.startDate
        update({ subjects, selectedSlotIds, selectedMonths, startDate })
        const stateId = state.funnelStateId || reduxFunnelStateId
        if (stateId) {
            props.apiRequest(
                summerFunnelService.updateAnswers,
                { stateId },
                {
                    // Persist matières + créneaux + mois + date de démarrage ici
                    // pour qu'une reprise sur un autre device garde toute la formule.
                    subjects,
                    slotIds: selectedSlotIds,
                    months: selectedMonths,
                    startDate,
                }
            )
        }
        amplitude.track("summer_funnel_configurator_completed", {
            funnel_state_id: stateId,
            nb_subjects: nbSubjects,
            slot_ids: selectedSlotIds,
            weekly_hours: weeklyHours,
            months: selectedMonths,
        })
        // Reveal du programme — généré sur les matières + créneaux choisis ici.
        navigation.navigate("summer/plan", nextParams)
    }

    const containerWidth = Math.min(dimensions?.width || 400, CONTENT_MAX)

    // Matières en chips compactes (sélection uniquement — la répartition par
    // matière vit désormais sur le reveal du programme).
    const subjectOptions = availableSubjects.map((s) => ({
        id: String(s.id),
        label: s.name,
        iconUri: subjectIconUri(s.icon_path),
    }))

    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                <BrevetFunnelHeader
                    step={4}
                    total={7}
                    hideLogo
                    onBack={() => navigation.goBack()}
                />
                <ScrollView
                    contentContainerStyle={styles.content}
                    showsVerticalScrollIndicator={false}
                >
                    <SummerEyebrow />
                    <Text style={styles.title}>On compose la formule</Text>

                    {/* 1. Mois souhaités (juillet/août — rien de pré-coché, le
                        « min 1 » est porté par le CTA) */}
                    <Text style={styles.sectionTitle}>Mois</Text>
                    <View style={styles.chipsRow}>
                        {SUMMER_MONTHS.map((m) => (
                            <SummerMonthChip
                                key={m.id}
                                label={m.label}
                                selected={selectedMonths.includes(m.id)}
                                onPress={() => toggleMonth(m.id)}
                            />
                        ))}
                    </View>

                    {/* 2. Créneaux — 5 jours × leurs horaires EN VENTE, tous d'1 h
                        (les créneaux saturés ne sont pas rendus) */}
                    <Text style={[styles.sectionTitle, { marginTop: 24 }]}>
                        Créneaux
                    </Text>
                    <Text style={styles.hint}>
                        Séances en petit groupe d'1 h — matin ou soir, autant de
                        jours que vous voulez.
                    </Text>
                    <View style={styles.dayList}>
                        {offeredSlotDays.map((d) => (
                            <View key={d.day} style={styles.dayBlock}>
                                <View style={styles.optionRow}>
                                    {d.options.map((opt) => {
                                        const selected = selectedSlotIds.includes(
                                            opt.id
                                        )
                                        return (
                                            <TouchableOpacity
                                                key={opt.id}
                                                activeOpacity={0.8}
                                                onPress={() => toggleSlot(opt.id)}
                                                style={[
                                                    styles.optionCard,
                                                    selected &&
                                                        styles.optionCardSelected,
                                                ]}
                                            >
                                                <Text
                                                    numberOfLines={1}
                                                    style={[
                                                        styles.optionText,
                                                        selected &&
                                                            styles.optionTextSelected,
                                                    ]}
                                                >
                                                    {opt.label}
                                                </Text>
                                            </TouchableOpacity>
                                        )
                                    })}
                                </View>
                            </View>
                        ))}
                    </View>
                    {error === "slot_locked_by_subject" && (
                        <Text style={styles.errorText}>
                            Retirez une matière d'abord pour libérer ce créneau.
                        </Text>
                    )}

                    {/* 3. Matières — plafonnées au nb de créneaux (≤ weeklyHours),
                        on peut en prendre MOINS (ex. 3h tout en maths). Le hint
                        dynamique « Jusqu'à N matières » monte quand on ajoute un
                        créneau → rend la causalité créneaux→matières évidente. */}
                    <Text style={[styles.sectionTitle, { marginTop: 24 }]}>
                        Matières
                    </Text>
                    <Text style={styles.hint}>
                        Jusqu'à {weeklyHours} matière
                        {weeklyHours > 1 ? "s" : ""}.
                    </Text>
                    {loadingSubjects && availableSubjects.length === 0 ? (
                        <View style={styles.subjectsLoading}>
                            <ActivityIndicator color={theme.colors.primary} />
                        </View>
                    ) : availableSubjects.length === 0 ? (
                        <Text style={styles.emptyText}>
                            Aucune matière disponible pour cette classe pour le
                            moment.
                        </Text>
                    ) : (
                        <ClassChipGridM
                            style={styles.subjectsChips}
                            options={subjectOptions}
                            selectedIds={subjects}
                            disabledIds={
                                subjects.length >=
                                Math.min(weeklyHours, SUMMER_MAX_SUBJECTS)
                                    ? subjectOptions
                                          .map((opt) => opt.id)
                                          .filter(
                                              (id) => !subjects.includes(id)
                                          )
                                    : []
                            }
                            onToggle={toggleSubject}
                        />
                    )}

                    {/* Tarif — suit le nombre de créneaux + la variante d'engagement
                        servie par le lien (défaut sans engagement). Rien de coché →
                        prix plancher « à partir de » dérivé de la grille chargée. */}
                    <View style={styles.priceCard}>
                        {hasSlots ? (
                            <View style={styles.priceCol}>
                                {/* Le prix à l'heure (arrondi à l'unité) devient
                                    l'ancre : au fil de l'été, « X €/mois » veut de
                                    moins en moins dire ce qu'on paie vraiment (mois
                                    d'entrée proratisé), alors que le €/h reste
                                    stable. Le détail par mois passe en sous-ligne. */}
                                <Text style={styles.priceHourly}>
                                    {formatEuros(
                                        Math.round(displayPricing.hourly)
                                    )}
                                    <Text style={styles.priceHourlyUnit}>/h</Text>
                                </Text>
                                <Text style={styles.pricePerMonth}>
                                    {perMonthSentence}
                                </Text>
                            </View>
                        ) : (
                            <Text style={styles.priceFloor}>
                                À partir de {formatEuros(floorPrice)}
                                <Text style={styles.priceFloorUnit}>/mois</Text>
                            </Text>
                        )}
                        <View style={styles.priceSecureRow}>
                            <FontAwesome5
                                name="lock"
                                size={11}
                                color={theme.colors.success}
                            />
                            <Text style={styles.priceSecureText}>
                                Paiement sécurisé à l'étape suivante
                            </Text>
                        </View>
                    </View>
                    {/* Début des cours — cliquable quand plusieurs options de
                        démarrage existent (le choix ajuste la 1ʳᵉ facture). */}
                    <SummerStartPicker
                        style={styles.startPicker}
                        options={startOptions}
                        selectedOption={selectedStartOption}
                        onSelect={onSelectStartOption}
                        monthlyPriceCents={startMonthlyPriceCents}
                        chosenMonths={selectedMonths}
                    />
                    {/* La mention « N h/semaine » n'a de sens qu'avec au moins un
                        créneau coché (état plancher : pas de « 0 h/semaine »). */}
                    <Text style={styles.priceNote}>
                        {priceNoteLead}.{" "}
                        {state.skipDeposit
                            ? "Vous finalisez l'inscription à l'étape suivante."
                            : "Vous bloquez la place pour 1 € à l'étape suivante."}
                    </Text>
                    <SummerHelpBanner step={4} />
                </ScrollView>
                <View style={styles.footer}>
                    {missingSelectionHint ? (
                        <Text style={styles.footerHint}>
                            {missingSelectionHint}
                        </Text>
                    ) : null}
                    <FilledButtonM
                        label="Voir le programme"
                        onPress={onContinue}
                        disabled={!selectionComplete}
                        color={
                            selectionComplete
                                ? theme.colors.primary
                                : theme.colors.lightMore
                        }
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    root: { flex: 1, alignItems: "center", backgroundColor: theme.colors.whiteAlmost },
    frame: { flex: 1, backgroundColor: theme.colors.white, paddingHorizontal: 20 },
    content: { paddingTop: 18, paddingBottom: 24 },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 18,
    },
    sectionTitle: {
        fontFamily: "Montserrat-Bold",
        fontSize: 16,
        color: theme.colors.primary,
        marginBottom: 4,
    },
    hint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        marginBottom: 10,
        lineHeight: 16,
    },
    subjectsChips: { marginTop: 4 },
    subjectsLoading: { paddingVertical: 24, alignItems: "center" },
    emptyText: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
        paddingVertical: 12,
    },
    chipsRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
    errorText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.error,
        marginTop: 8,
    },
    // Créneaux : 5 lignes (jour), chaque ligne = 2 options pleine largeur. Chaque
    // option porte le jour ("Lundi 10h30-11h30") → plus de header de jour séparé,
    // bloc plus compact verticalement.
    dayList: { marginTop: 4, gap: 8 },
    dayBlock: {},
    // Une ligne = les horaires EN VENTE du jour, en cartes `flex: 1`. Calibré pour
    // 2 horaires (le libellé « Lundi 11h30-12h30 » tient sur une ligne). Rouvrir un
    // 3ᵉ horaire tronquerait les libellés → passer la ligne en wrap à ce moment-là.
    optionRow: { flexDirection: "row", gap: 10 },
    optionCard: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        borderRadius: 12,
        paddingVertical: 10,
        paddingHorizontal: 4,
        backgroundColor: theme.colors.white,
    },
    optionCardSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    optionText: {
        fontFamily: "Montserrat-Bold",
        // Réduit (vs 14) pour que "Lundi 10h30-11h30" tienne sur une ligne.
        fontSize: 12,
        color: theme.colors.grey,
    },
    optionTextSelected: { color: theme.colors.primary },
    priceCard: {
        alignItems: "center",
        marginTop: 24,
        padding: 16,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.whiteAlmost,
    },
    priceCol: { alignItems: "center" },
    // Ancre = prix à l'heure arrondi, en gros.
    priceHourly: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 22,
        color: theme.colors.primary,
        textAlign: "center",
    },
    priceHourlyUnit: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.primary,
    },
    // Détail « ce qu'on paie par mois » — petit, sous l'ancre.
    pricePerMonth: {
        fontFamily: "Montserrat-SemiBold",
        fontSize: 11,
        color: theme.colors.grey,
        marginTop: 3,
        textAlign: "center",
    },
    // État plancher (aucun créneau coché).
    priceFloor: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 18,
        color: theme.colors.primary,
        textAlign: "center",
    },
    priceFloorUnit: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.primary,
    },
    priceSecureRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        marginTop: 8,
    },
    priceSecureText: {
        fontFamily: "Montserrat-SemiBold",
        fontSize: 11,
        color: theme.colors.success,
    },
    priceNote: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.grey,
        marginTop: 8,
        lineHeight: 16,
    },
    startPicker: { marginTop: 10 },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    footerHint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        textAlign: "center",
        marginBottom: 8,
    },
})

const mapStateToProps = (state) => ({
    reduxFunnelStateId: state.data?.summerFunnel?.funnelStateId ?? null,
})

const mapDispatchToProps = { apiRequest }

export default connect(mapStateToProps, mapDispatchToProps)(SummerConfiguratorScreen)
